import { useEffect, useState } from 'react';
import axios from '../api/axiosClient';
import { API_BASE_URL } from '../api/constant';

/**
 * Tiny in-memory cache for the public store settings.
 *
 * The /api/store-settings/public endpoint returns a flat key→value map
 * with a small set of allow-listed keys (WhatsApp number, business
 * hours, compra-mínima threshold, etc.). We fetch once per browser
 * session and reuse the result across every component that calls
 * `usePublicStoreSettings()` — there's no need for cache invalidation
 * since settings change ~weekly at most and a hard refresh is enough.
 *
 * Returns `{ data, loading, error }` where `data` is the settings map
 * (an empty object until the fetch completes).
 *
 * Public surface kept intentionally narrow — anything that needs richer
 * caching/invalidation can move to react-query later without breaking
 * callers.
 */
let cache = null;
let inflight = null;

async function fetchPublicSettings() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/store-settings/public`);
      cache = res && res.data && typeof res.data === 'object' ? res.data : {};
      if (typeof window !== 'undefined') {
        window.__PINKSISLY_STORE_SETTINGS__ = cache;
      }
    } catch (err) {
      // Network/down — degrade gracefully. The cart still works without
      // the settings; the only loss is the compra-mínima gate (which the
      // server still enforces on order create as a safety net).
      console.warn('usePublicStoreSettings: fetch failed', err?.message || err);
      cache = {};
      if (typeof window !== 'undefined') {
        window.__PINKSISLY_STORE_SETTINGS__ = cache;
      }
    } finally {
      inflight = null;
    }
    return cache;
  })();
  return inflight;
}

export const usePublicStoreSettings = () => {
  const [state, setState] = useState({ data: cache || {}, loading: !cache, error: null });
  useEffect(() => {
    if (cache) return;
    let alive = true;
    fetchPublicSettings().then((data) => {
      if (alive) setState({ data, loading: false, error: null });
    });
    return () => {
      alive = false;
    };
  }, []);
  return state;
};

/**
 * Convenience: the seasonal collection entry (Menú 3 in Configuración).
 * The ROPAS catalog is shown under this owner-editable name in the nav,
 * footer, breadcrumbs and the 404 CTA — reading it from one place keeps
 * them in sync when the owner renames the season (FW2026 → SS2027 → …).
 * Fallbacks apply while the fetch is in flight or if it fails.
 */
export const useSeasonNavEntry = () => {
  const { data, loading } = usePublicStoreSettings();
  const label = (data?.['nav.item3.label'] || '').trim() || 'SS2027';
  const to = (data?.['nav.item3.url'] || '').trim() || '/ropas';
  return { label, to, loading };
};

/**
 * Convenience: parse `order.minimum_amount_ars` into a positive number,
 * or return 0 when the feature is disabled (empty / non-numeric /
 * non-positive). 0 is the universal "no gate" sentinel.
 */
export const useOrderMinimumAmount = () => {
  const { data, loading } = usePublicStoreSettings();
  const raw = data?.['order.minimum_amount_ars'];
  const parsed = raw != null && raw !== '' ? Number(raw) : 0;
  const value = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  return { minimum: value, loading };
};
